import {
  useAnimalDeletionSummary,
  useDeleteAnimal,
  type AnimalSummary,
} from '@/api/hooks';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { getErrorMessage } from '@/lib/api';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

export function DeleteAnimalModal({
  animal,
  propertyAnimals,
  open,
  onClose,
  onDeleted,
}: {
  animal: AnimalSummary | null;
  propertyAnimals?: AnimalSummary[];
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const deleteAnimal = useDeleteAnimal();
  const { data: summary, isLoading, isError, refetch } = useAnimalDeletionSummary(
    animal,
    propertyAnimals,
    open && !!animal,
  );
  const [error, setError] = useState('');

  async function handleDelete() {
    if (!animal) return;
    setError('');
    try {
      await deleteAnimal.mutateAsync(animal.id);
      onClose();
      onDeleted?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao excluir animal.'));
    }
  }

  const displayName = animal
    ? [animal.internal_number, animal.animal_name].filter(Boolean).join(' · ')
    : '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Excluir animal"
      size="md"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={deleteAnimal.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleDelete}
            loading={deleteAnimal.isPending}
            disabled={isLoading}
          >
            Excluir animal
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-manejai-bark">
          Tem certeza que deseja excluir o animal{' '}
          <span className="font-medium text-manejai-forest">{displayName}</span>?
        </p>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-4 rounded bg-manejai-sand/50 animate-pulse" />
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium">Não foi possível verificar os registros relacionados.</p>
                <p className="mt-2">
                  A exclusão pode remover fotos, eventos e movimentações vinculados a este animal.
                  Esta ação não pode ser desfeita.
                </p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="mt-2 font-medium underline"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>
        ) : summary?.uncertain ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <div>
                {summary.has_related_records ? (
                  <>
                    <p className="font-medium">Registros encontrados (verificação parcial):</p>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      {summary.related.map((item) => (
                        <li key={item.key}>
                          {item.count} {item.label.toLowerCase()}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="font-medium">
                    Não foi possível verificar todos os registros na API atual.
                  </p>
                )}
                <p className="mt-2">
                  A exclusão pode remover fotos, eventos e movimentações vinculados.
                  Esta ação não pode ser desfeita.
                </p>
              </div>
            </div>
          </div>
        ) : summary?.has_related_records ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium">Este animal possui registros relacionados:</p>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  {summary.related.map((item) => (
                    <li key={item.key}>
                      {item.count} {item.label.toLowerCase()}
                    </li>
                  ))}
                </ul>
                <p className="mt-3">
                  Todos esses dados serão perdidos permanentemente, exceto os animais filhos —
                  eles permanecem no rebanho, mas perdem o vínculo genealógico com este animal.
                  Esta ação não pode ser desfeita.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-manejai-bark/70">
            Este animal não possui outros registros vinculados. A exclusão não pode ser desfeita.
          </p>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
